import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { DownloadIcon, UploadIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { Group, Menu } from '@calendly/ui/components/menu';
import { BodyText } from '@calendly/ui/components/typography';
import { ViewSwitcher } from '@calendly/ui/components/view-switcher';
import classNames from 'classnames';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Layout, LayoutItem } from 'components/layout';
import { useEventTypeContext } from 'features/event_type_provider';
import { useTrackAvailabilityInteraction } from 'hooks/analytics_events/use_track_availability_interaction';
import { usePopupSwitcher } from 'hooks/use_popup_switcher';
import { isEnterKey } from 'services/keycodes';
import { CopyFromContent, CopyFromPopup } from './copy_from';
import { CopyToContent, CopyToPopup } from './copy_to';
import { DropdownContainer } from './styles';
import { useHasFormErrors } from '../../../../../hooks';
const menuContainerCss = "m82y8wn";
const MenuContainer = forwardRef((props, ref) => <Menu ref={ref} {...props} className={classNames(menuContainerCss, props.className)} />);
const scrollableGroupCss = "s1et9xc8";
const ScrollableGroup = forwardRef((props, ref) => <Group ref={ref} {...props} className={classNames(scrollableGroupCss, props.className)} />);
const MenuHeader = () => <LayoutItem paddingTop="8" paddingX="16">
    <BodyText weight="bold">
      {I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.copy_from_to.title')}
    </BodyText>
  </LayoutItem>;
const Content = props => <Layout direction="column" gap="12" {...props} />;
const ContentLoader = () => <Layout grow justify="center" align="center"><Loader /></Layout>;
const ButtonsLayout = props => <Layout gap="8" paddingY="8" paddingX="16" {...props} />;
const trackActions = {
  'copy-to': 'apply_schedule',
  'copy-from': 'copy_schedule_from'
};
const getOptions = () => [{
  value: 'copy-to',
  icon: <UploadIcon />,
  text: I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.copy_from_to.tab_copy_to')
}, {
  value: 'copy-from',
  icon: <DownloadIcon />,
  text: I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.copy_from_to.tab_copy_from')
}];
export const DropdownContent = ({
  toggleDropdown,
  userId,
  ...props
}) => {
  const menuRef = useRef();
  const hasErrors = useHasFormErrors();
  const {
    eventType,
    isCreation
  } = useEventTypeContext();
  const [tab, setTab] = useState(isCreation ? 'copy-from' : 'copy-to');
  const [selectedETs, setSelectedETs] = useState([]);
  const {
    openPopup,
    closePopup,
    isPopupOpen
  } = usePopupSwitcher();
  const {
    isSubmitting
  } = useFormContext();
  const trackApply = useTrackAvailabilityInteraction({
    eventType,
    location: 'et_editor',
    action: trackActions[tab],
    updated_event_type_id: selectedETs.map(({
      id
    }) => id)
  });
  const applyDisabled = isCreation && isPopupOpen('copy-to') || !selectedETs.length;
  useEffect(() => {
    if (hasErrors) {
      toggleDropdown();
    }
  }, [hasErrors, toggleDropdown]);
  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, []);
  const handleSwitch = e => {
    setSelectedETs([]);
    setTab(e.target.value);
  };
  const handleApply = event => {
    trackApply();
    return openPopup(tab)(event);
  };

  // A dark magic 🧙:
  // This prevents dropdown from closing when user press Esc, Enter (or other control key) in the search
  // Also for Enter it prevent it from bubbling up and submit the form
  const stopEventBubbling = event => {
    if (isEnterKey(event)) {
      event.preventDefault();
    }
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };
  return <DropdownContainer isV4Design {...props}>
      <MenuContainer ref={menuRef}>
        <Group>
          <MenuHeader />
        </Group>
        {isSubmitting ? <ContentLoader /> : <ScrollableGroup>
            <Content onKeyDown={stopEventBubbling} onKeyPress={stopEventBubbling} onKeyUp={stopEventBubbling}>
              <Layout paddingX="16">
                <ViewSwitcher name="copy-view-switcher" value={tab} options={getOptions()} onChange={handleSwitch} />
              </Layout>
              {tab === 'copy-to' && <CopyToContent selectedETs={selectedETs} setSelectedETs={setSelectedETs} userId={userId} />}
              {tab === 'copy-from' && <CopyFromContent selectedETs={selectedETs} setSelectedETs={setSelectedETs} userId={userId} />}
            </Content>
          </ScrollableGroup>}
        <Group>
          <ButtonsLayout>
            <Button size="small" onClick={handleApply} disabled={applyDisabled}>{I18n.t('publisher.action.apply')}</Button>
            <Button size="small" onClick={toggleDropdown} decoration="ghost">{I18n.t('publisher.action.cancel')}</Button>
          </ButtonsLayout>
        </Group>
      </MenuContainer>
      {isPopupOpen('copy-from') && <CopyFromPopup onClose={closePopup} toggleDropdown={toggleDropdown} selectedETs={selectedETs} userId={userId} />}
      {isPopupOpen('copy-to') && <CopyToPopup onClose={closePopup} toggleDropdown={toggleDropdown} selectedETs={selectedETs} userId={userId} />}
    </DropdownContainer>;
};

require("./dropdown_content.wyw-in-js.css!=!../../../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./dropdown_content.js");